import React from 'react';
import { useEmailSettings } from '../hooks/useEmailSettings';
import { defaultTemplates } from '../constants/defaultTemplates';
import ReminderControls from './ReminderControls';
import NotificationPreferences from './NotificationPreferences';
import SimpleEmailTemplateEditor from './simple/SimpleEmailTemplateEditor';
import './EmailSettingsPanel.css';

const TEMPLATE_OPTIONS = [
  {
    key: 'bookingConfirmation',
    title: 'Booking confirmation',
    description: 'Sent when an appointment is confirmed.',
    mergeFields: ['customerName', 'service', 'date', 'time', 'portalUrl']
  },
  {
    key: 'scheduleChange',
    title: 'Schedule change',
    description: 'Sent when an appointment moves to a new time.',
    mergeFields: ['customerName', 'service', 'oldDate', 'oldTime', 'newDate', 'newTime', 'portalUrl']
  },
  {
    key: 'depositConfirmation',
    title: 'Deposit confirmation',
    description: 'Sent after a deposit payment is received.',
    mergeFields: ['customerName', 'service', 'date', 'time', 'depositAmount', 'remainingBalance', 'totalAmount']
  },
  {
    key: 'balanceConfirmation',
    title: 'Balance confirmation',
    description: 'Sent when the remaining balance is paid.',
    mergeFields: ['customerName', 'service', 'date', 'time', 'balanceAmount', 'totalAmount']
  },
  {
    key: 'checkInLinkRequest',
    title: 'Check-in link',
    description: 'Sent automatically before appointments when check-in email reminders are enabled.',
    mergeFields: ['customerName', 'service', 'date', 'time', 'checkInUrl']
  }
];

const REMINDER_TEMPLATE = {
  key: 'appointmentReminder',
  title: 'Reminder email',
  description: 'The email clients receive before their appointment.',
  mergeFields: ['customerName', 'service', 'date', 'time', 'portalUrl']
};

const EmailSettingsPanel = ({ companyId }) => {
  const [activeTemplateKey, setActiveTemplateKey] = React.useState(TEMPLATE_OPTIONS[0].key);
  const {
    settings,
    companyData,
    loading,
    error,
    saving,
    updateSettings,
    sendTestEmail
  } = useEmailSettings(companyId);

  if (loading && !settings) {
    return (
      <div className="email-settings-loading">
        <div className="email-settings-spinner"></div>
      </div>
    );
  }

  if (error && !settings) {
    return <div className="email-settings-error">{error}</div>;
  }

  const activeTemplate = TEMPLATE_OPTIONS.find((option) => option.key === activeTemplateKey) || TEMPLATE_OPTIONS[0];

  const handleSettingsUpdate = (newSettings) => {
    updateSettings(newSettings);
  };

  const updateTemplate = (key, template) => {
    handleSettingsUpdate({
      ...settings,
      templates: {
        ...settings.templates,
        [key]: template
      }
    });
  };

  return (
    <div className="email-settings-panel">
      <div className="email-settings-container">
        <div className="email-settings-page-header">
          <div>
            <h1 className="email-settings-page-title">Email settings</h1>
            <p className="email-settings-page-description">
              Manage email preferences, templates, and reminders from one cleaner page.
            </p>
          </div>
        </div>

        {error && settings && <div className="email-settings-error">{error}</div>}

        <div className="email-settings-content">
          <section className="email-settings-section-card">
            <div className="email-settings-section-header">
              <h2 className="email-settings-section-title">Preferences</h2>
              <p className="email-settings-section-description">
                Choose which emails are enabled for clients.
              </p>
            </div>
            <NotificationPreferences
              settings={settings}
              onUpdate={handleSettingsUpdate}
            />
          </section>

          <section className="email-settings-section-card">
            <div className="email-settings-section-header">
              <h2 className="email-settings-section-title">Templates</h2>
              <p className="email-settings-section-description">
                Pick an email, edit the subject and body, then apply your changes.
              </p>
            </div>

            <div className="email-settings-template-layout">
              <div className="email-settings-template-list" role="tablist" aria-label="Email templates">
                {TEMPLATE_OPTIONS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={`email-settings-template-tab ${activeTemplateKey === option.key ? 'active' : ''}`}
                    onClick={() => setActiveTemplateKey(option.key)}
                  >
                    <span>{option.title}</span>
                    <small>{option.description}</small>
                  </button>
                ))}
              </div>

              <div className="email-settings-editor-card">
                <SimpleEmailTemplateEditor
                  key={activeTemplate.key}
                  title={activeTemplate.title}
                  description={activeTemplate.description}
                  template={settings?.templates?.[activeTemplate.key]}
                  defaultTemplate={defaultTemplates.templates[activeTemplate.key]}
                  mergeFields={activeTemplate.mergeFields}
                  onApply={(template) => updateTemplate(activeTemplate.key, template)}
                  onTestEmail={(email) => sendTestEmail(activeTemplate.key, email)}
                  saving={saving}
                  companyData={companyData}
                />
              </div>
            </div>
          </section>

          <section className="email-settings-section-card">
            <div className="email-settings-section-header">
              <h2 className="email-settings-section-title">Reminders</h2>
              <p className="email-settings-section-description">
                Keep reminder timing and reminder copy together so this section is easier to manage.
              </p>
            </div>

            <div className="email-settings-reminder-grid">
              <div className="email-settings-editor-card">
                <ReminderControls
                  enabled={settings?.reminderEnabled}
                  reminders={settings?.reminders || []}
                  settings={{ ...settings, companyId }}
                  onUpdate={(reminderSettings) => {
                    handleSettingsUpdate({
                      ...settings,
                      ...reminderSettings
                    });
                  }}
                />
              </div>

              <div className="email-settings-editor-card">
                <SimpleEmailTemplateEditor
                  key={REMINDER_TEMPLATE.key}
                  title={REMINDER_TEMPLATE.title}
                  description={REMINDER_TEMPLATE.description}
                  template={settings?.templates?.appointmentReminder}
                  defaultTemplate={defaultTemplates.templates.appointmentReminder}
                  mergeFields={REMINDER_TEMPLATE.mergeFields}
                  onApply={(template) => updateTemplate('appointmentReminder', template)}
                  onTestEmail={(email) => sendTestEmail('appointmentReminder', email)}
                  saving={saving}
                  companyData={companyData}
                />
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default EmailSettingsPanel;